import React, { useState } from 'react';
import { IProposal } from 'interfaces/proposals/proposal';
import useModal from 'helpers/useModal';
import { daysLeftFilter, vestsFilter } from 'helpers/filters';
import { Link } from "react-router-dom";
import { useAuth } from 'helpers/authContext';
import { isLoggedWithKeychain } from 'services/dsteem-service';
import { proposalWifVote } from 'helpers/steem';
import { keychainProposalVote } from 'helpers/keychain';
import LoginModal from 'features/user/userModals/LoginModal';
import { useToast } from 'components/ui/use-toast';

import {
  Card,
  CardBody,
  CardTitle,
  Row,
  Col,
  Badge,
  UncontrolledTooltip 
} from "reactstrap";

type Props = {
  proposal: IProposal,
  style: string,
  reloadProposals: (silent?: boolean) => Promise<void>
} 

export const ProposalListItem = ({ proposal, style, reloadProposals }: Props) => {
  const { proposals, autologin, user } = useAuth();   
  const { toast } = useToast();
  const { isOpenLoginModal, toggleLoginModal } = useModal();
  
  const [isVoting, setIsVoting] = useState(false);
  const [requiredActive, setRequiredActive] = useState(false);
  const [pendingVoteStatus, setPendingVoteStatus] = useState<boolean | null>(null);

  const keychainLogin = isLoggedWithKeychain(autologin);
  const isVoted = proposals?.length > 0 && proposals.includes(proposal.id);

  const copyLink = () => {
    const baseUrl = window.location.origin;
    const link = `${baseUrl}/proposals?op=voteProposal&id=${proposal.id}`;
    navigator.clipboard.writeText(link);
    toast({
        title: "Link Copied",
        description: "The proposal voting link has been copied to your clipboard.",
        variant: "wild_green"
    });
  };

  const handleVote = async (voteStatus: boolean, wif = "") => {
    if (!user) {
      toast({ title: "Login required", description: "Please log in to vote on proposals.", variant: "rose_red" });
      return;
    }

    setIsVoting(true);
    try {
      if (keychainLogin) {
        const success = await keychainProposalVote(voteStatus, user, proposal.id);
        if (success === true) {
          toast({ title: "Success", description: voteStatus ? "Proposal supported!" : "Support removed.", variant: "wild_green" });
          await reloadProposals(true);
        } else {
          toast({ title: "Error", description: "Failed to process vote with Keychain.", variant: "rose_red" });
        }
      } else {
        const res: any = await proposalWifVote(voteStatus, autologin, proposal.id, wif);
        if (res.success) {
          toast({ title: "Success", description: voteStatus ? "Proposal supported!" : "Support removed.", variant: "wild_green" });
          await reloadProposals(true);
        } else {
          if (res?.result?.message?.toString().includes("Missing Active Authority")) {
            setRequiredActive(true);
            setPendingVoteStatus(voteStatus);
            toggleLoginModal();
          } else {
            toast({ title: "Error", description: res.result || "Failed to process vote.", variant: "rose_red" });
          }
        }
      }
    } catch (error) {
      console.error("Voting error:", error);
      toast({ title: "Error", description: "An unexpected error occurred.", variant: "rose_red" });
    } finally {
      setIsVoting(false);
    }
  };

  const voteButton = (
    <div 
      className={`icon icon-shape ${isVoted ? 'bg-voted' : 'bg-secondary2'} text-dark rounded-circle shadow upvote-icon d-flex align-items-center justify-content-center`} 
      style={{ cursor: isVoting ? 'default' : 'pointer', opacity: isVoting ? 0.7 : 1, width: '45px', height: '45px' }}
      onClick={() => !isVoting && handleVote(!isVoted)}
    >
      {isVoting ? (
        <span className="spinner m-0" />
      ) : (
        <i className="fas fa-angle-up" style={{ fontSize: '1.2rem' }}></i>
      )}
    </div>
  );

  return (
    <div>
      <Card 
        className={`card-stats mb-4 mt-4 mb-lg-0 ${style} shadow-sm card-proposals`} 
        key={proposal.id}
      >
        <CardBody>
          <Row>
            <Col className="col-10 col-lg-11">
              <Row>
                <Col className="d-none d-lg-block col-lg-1 my-auto divider">
                  <Link
                    className="avatar rounded-circle mr-3"
                    to={`/u/${proposal.creator}`}
                    target="_blank"
                  >
                    <img
                      alt="..."
                      src={`https://steemitimages.com/u/${proposal.creator}/avatar`}
                    />
                  </Link>
                </Col>
                <Col className="col-sm-12 col-md-11">
                  <CardTitle className="mb-0 d-flex align-items-center flex-wrap">
                    <Link className="text mr-2 font-weight-bold" style={{ color: 'var(--letters)' }} to={`/p/${proposal.creator}/${proposal.permlink}`} target="_blank">{proposal.subject}</Link>
                    <Link to={`/p/${proposal.creator}/${proposal.permlink}`} style={{ color: 'var(--speem)' }} target="_blank"><small>(#{proposal.proposal_id})</small></Link>
                    <i 
                      className="fas fa-link ml-2 text-muted cursor-pointer" 
                      title="Copy Vote Link" 
                      onClick={copyLink}
                    />
                    {proposal.receiver === "steem.dao" ?
                      <span>
                        <Badge color="success" className="ml-2" id="returnTooltip">Return proposal</Badge>
                        <UncontrolledTooltip
                          delay={0}
                          placement="top"
                          target="returnTooltip"
                        >
                          Return proposal acts as a threshold you need to pass to start receiving funding. Every proposal with less votes will not receive funds.
                      </UncontrolledTooltip>
                      </span>
                      : null
                    }
                    {proposal.receiver === "null" ?
                      <span>
                        <Badge color="warning" className="ml-2" id="burnTooltip">Burn proposal</Badge>
                        <UncontrolledTooltip
                          delay={0}
                          placement="top"
                          target="burnTooltip"
                        >
                          Burn proposal decreases SBD inflation, the funds will be sent to unrecoverable account (@null).
                      </UncontrolledTooltip>
                      </span>
                      : null
                    }
                  </CardTitle>
                  <p className="mt-1 mb-0">
                    <span className="text-sm mr-1" style={{ color: 'var(--letters)', opacity: 0.7 }}>
                      by <Link to={`/u/${proposal.creator}`} className="mr-1 font-weight-bold" style={{ color: 'var(--speem)' }} target="_blank">@{proposal.creator}</Link>
                      {proposal.creator !== proposal.receiver ?
                        <span>
                          with receiver <Link to={`/u/${proposal.receiver}`} style={{ color: 'var(--speem)' }} target="_blank">@{proposal.receiver}</Link>
                        </span>
                        : null
                      }
                    </span>
                    <Badge className="mr-2 border shadow-none badge-proposals">{vestsFilter(proposal.total_votes.toString())} SP</Badge>
                    <Badge className="mr-2 border shadow-none badge-proposals">{proposal.daily_pay.amount} SBD/day</Badge>
                    <Badge className="mr-2 border shadow-none badge-proposals">{daysLeftFilter(proposal.end_date)}</Badge>
                  </p>
                </Col>
              </Row>
            </Col>
            <Col className="col-2 col-lg-1 d-flex align-items-center justify-content-center p-0">
              {voteButton}
            </Col>
          </Row>
        </CardBody>
      </Card>
      
      <LoginModal
        isShowing={isOpenLoginModal}
        hide={toggleLoginModal}
        requiredActive={requiredActive}
        onSuccess={({ wif }) => {
          if (wif && pendingVoteStatus !== null) {
            handleVote(pendingVoteStatus, wif);
          }
        }}
      />
    </div>
  )
}

export default ProposalListItem;
